import { useContext } from 'react';
import classNames from 'classnames';
import Image from '@next/image';

import { ApiNumber } from 'types/models/ApiNumber';
import { Brand } from 'types/models/Brand';
import { ConfigContext } from '@providers/ConfigProvider';
import { FilesContext } from '@providers/FilesProvider';
import RenderInView from '@templates/RenderInView';
import { Props as ClientAboutUsProps } from '@components/ClientsAboutUs/ClientsAboutUs';
import HeroSection from '@components/molecules/HeroSection';
import ClientsAboutUs from '@components/ClientsAboutUs';
import StaticPageFirstSection, { firstSectionItems } from '@components/StaticPageFirstSection';
import { FormattedMessage } from 'utils/intl';
import { getNumber, getNumbers } from 'utils/getNumber';

import BadgesSection from '../components/BadgesSection';
import WhyTrustUs from '../components/WhyTrustUs';
import ProsSection from '../components/ProsSection';
import OurWay from '../components/OurWay';
import OurBrands from '../components/OurBrands';
import Info from '../components/Info';

import classes from '../AboutUs.module.scss';
import { getItems } from './AboutUsCommon.utils';
import messages from './AboutUsCommon.messages';

type Props = {
  testimonials?: ClientAboutUsProps['testimonials'];
  numbers?: ApiNumber[];
  brands?: Brand[];
};

const AboutUsCommon = ({ testimonials, numbers = [], brands = [] }: Props): JSX.Element => {
  const { getPageImageConfigs } = useContext(FilesContext);
  const { contact } = useContext(ConfigContext);
  const [team] = getPageImageConfigs(['team']);

  const coconStartYear = getNumber(numbers, 'COCON_START_YEAR');

  return (
    <>
      <HeroSection
        title={<FormattedMessage {...messages.heroTitle} />}
        subtitle={<FormattedMessage {...messages.heroSubtitle} />}
      />
      <div className={classes.container}>
        <StaticPageFirstSection
          title={<FormattedMessage {...messages.firstSectionTitle} values={{ year: coconStartYear }} />}
          description={<FormattedMessage {...messages.firstSectionDescription} />}
          items={firstSectionItems}
        />
        {numbers.length && (
          <WhyTrustUs
            className={classes.whyTrustUs}
            title={<FormattedMessage {...messages.whyTrustTitle} />}
            subtitle={<FormattedMessage {...messages.whyTrustSubtitle} />}
            items={getItems([
              contact.name,
              ...getNumbers(numbers, ['COCON_START_YEAR', 'DELLOWO_START_YEAR', 'CLIENTS', 'DEVICES_PER_YEAR']),
            ])}
          />
        )}
        {testimonials && testimonials.length > 0 && (
          <RenderInView>
            {({ ref, inView }) => <div ref={ref}>{inView && <ClientsAboutUs testimonials={testimonials} />}</div>}
          </RenderInView>
        )}
        <BadgesSection className={classes.section} />
        <ProsSection />
        <OurWay className={classNames(classes.section, classes.ourWaySection)} year={coconStartYear} />
        <div className={classes.team}>
          {team && <Image width="100%" objectFit="contain" title="team" alt="team" resolveConfig={team} />}
        </div>
        <OurBrands className={classes.section} numbers={numbers} brands={brands} />
        <Info className={classes.section} />
      </div>
    </>
  );
};

export default AboutUsCommon;
